(() => {
  const hceI18n = window.hceI18n || {};
  'use strict';

  const CONTAINER_SELECTOR = '#ProductGridContainer';
  const GRID_SELECTOR = '#product-grid';
  const PAGINATION_SELECTOR = '.pagination-wrapper';
  const NEXT_SELECTOR = '.pagination__item--next';
  const LOAD_MORE_SELECTOR = '[data-hce-load-more]';
  const LOAD_BUTTON_SELECTOR = '[data-hce-load-more-button]';
  const STATE_PREFIX = 'hce:collection-grid:';

  let mutationTimer;
  let restoreStarted = false;

  const normalizedStateKey = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    return `${STATE_PREFIX}${url.pathname}${url.search ? url.search : ''}`;
  };

  const getContainer = () => document.querySelector(CONTAINER_SELECTOR);
  const getGrid = (container = getContainer()) => container?.querySelector(GRID_SELECTOR) || null;
  const getPagination = (container = getContainer()) => container?.querySelector(PAGINATION_SELECTOR) || null;

  const getNextUrl = (container = getContainer()) => {
    const next = container?.querySelector(NEXT_SELECTOR);
    return next?.getAttribute('href') || '';
  };

  const getSectionId = (container = getContainer()) => {
    const grid = getGrid(container);
    if (grid?.dataset?.id) return grid.dataset.id;
    const section = container?.closest?.('[id^="shopify-section-"]');
    return section?.id?.replace(/^shopify-section-/, '') || '';
  };

  const getCurrentPage = (container = getContainer()) => {
    const current = container?.querySelector('.pagination__item--current');
    const page = Number.parseInt((current?.textContent || '1').trim(), 10);
    return Number.isFinite(page) && page > 0 ? page : 1;
  };

  const prioritizeAvailableProducts = () => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('sort_by')) return;

    document.querySelectorAll(GRID_SELECTOR).forEach((grid) => {
      if (grid.querySelector('.collection_grid_inline_image')) return;
      const products = Array.from(grid.children).filter((item) => item.matches('[data-hce-available]'));
      if (products.length < 2) return;
      const available = products.filter((item) => item.dataset.hceAvailable === 'true');
      const soldOut = products.filter((item) => item.dataset.hceAvailable !== 'true');
      [...available, ...soldOut].forEach((item) => grid.appendChild(item));
    });
  };

  const getLoadedPages = (loadMore) => {
    const value = Number.parseInt(loadMore?.dataset?.loadedPages || '1', 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  };

  const saveState = () => {
    const loadMore = document.querySelector(LOAD_MORE_SELECTOR);
    if (!loadMore) return;

    const state = {
      loadedPages: getLoadedPages(loadMore),
      scrollY: Math.max(0, Math.round(window.scrollY || 0))
    };

    try {
      sessionStorage.setItem(normalizedStateKey(), JSON.stringify(state));
    } catch (error) {}

    const existingState = history.state && typeof history.state === 'object' ? history.state : {};
    history.replaceState({ ...existingState, hceCollectionLoadedPages: state.loadedPages }, '', window.location.href);
  };

  const readState = () => {
    try {
      const raw = sessionStorage.getItem(normalizedStateKey());
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !Number.isFinite(Number(parsed.loadedPages))) return null;
      return {
        loadedPages: Math.max(1, Number(parsed.loadedPages)),
        scrollY: Math.max(0, Number(parsed.scrollY || 0))
      };
    } catch (error) {
      return null;
    }
  };

  const buildLoadMore = (container) => {
    const pagination = getPagination(container);
    const nextUrl = getNextUrl(container);
    if (!pagination || !nextUrl) return null;

    let loadMore = container.querySelector(LOAD_MORE_SELECTOR);
    if (!loadMore) {
      loadMore = document.createElement('div');
      loadMore.className = 'hce-load-more';
      loadMore.dataset.hceLoadMore = 'true';
      loadMore.dataset.loadedPages = String(getCurrentPage(container));
      loadMore.innerHTML = `
        <button type="button" class="hce-load-more__button" data-hce-load-more-button aria-controls="product-grid">
          LOAD MORE
        </button>
        <p class="visually-hidden" role="status" aria-live="polite" data-hce-load-more-status></p>
      `;
      pagination.parentNode.insertBefore(loadMore, pagination);
    }

    loadMore.dataset.nextUrl = nextUrl;
    loadMore.dataset.sectionId = getSectionId(container);
    loadMore.hidden = false;
    pagination.hidden = true;
    return loadMore;
  };

  const enhance = () => {
    const container = getContainer();
    if (!container) return;
    const loadMore = buildLoadMore(container);
    if (!loadMore) return;
    prioritizeAvailableProducts();
  };

  const updateFallbackPagination = (container, incomingDoc) => {
    const currentPagination = getPagination(container);
    const incomingPagination = incomingDoc.querySelector(PAGINATION_SELECTOR);

    if (!incomingPagination) {
      currentPagination?.remove();
      return;
    }

    incomingPagination.hidden = true;
    if (currentPagination) currentPagination.replaceWith(incomingPagination);
    else container.querySelector('.collection')?.appendChild(incomingPagination);
  };

  const loadNext = async ({ restoring = false } = {}) => {
    const container = getContainer();
    const grid = getGrid(container);
    const loadMore = container?.querySelector(LOAD_MORE_SELECTOR);
    const button = loadMore?.querySelector(LOAD_BUTTON_SELECTOR);
    const status = loadMore?.querySelector('[data-hce-load-more-status]');
    const nextUrl = loadMore?.dataset?.nextUrl || '';

    if (!container || !grid || !loadMore || !button || !nextUrl || button.dataset.loading === 'true') return false;

    const sectionId = loadMore.dataset.sectionId || getSectionId(container);
    if (!sectionId) return false;

    button.dataset.loading = 'true';
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    const originalLabel = button.textContent;
    button.textContent = hceI18n.loading || 'Loading…';

    try {
      const requestUrl = new URL(nextUrl, window.location.origin);
      requestUrl.searchParams.set('section_id', sectionId);

      const response = await fetch(requestUrl.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const incomingGrid = doc.querySelector(GRID_SELECTOR);
      if (!incomingGrid) throw new Error('Next product grid was not found.');

      const incomingItems = Array.from(incomingGrid.children);
      const loadedCount = incomingItems.length;
      const fragment = document.createDocumentFragment();
      incomingItems.forEach((item) => fragment.appendChild(item));
      grid.appendChild(fragment);

      const incomingNext = doc.querySelector(NEXT_SELECTOR)?.getAttribute('href') || '';
      updateFallbackPagination(container, doc);

      loadMore.dataset.loadedPages = String(getLoadedPages(loadMore) + 1);
      if (incomingNext) {
        loadMore.dataset.nextUrl = incomingNext;
        loadMore.hidden = false;
      } else {
        delete loadMore.dataset.nextUrl;
        loadMore.hidden = true;
      }

      prioritizeAvailableProducts();
      document.dispatchEvent(new CustomEvent('hce:collection-grid-updated', {
        detail: { source: restoring ? 'restore' : 'load-more', loadedCount }
      }));

      if (status) status.textContent = `${loadedCount} ${hceI18n.moreProductsLoaded || 'more products loaded.'}`;
      if (!restoring) saveState();
      return true;
    } catch (error) {
      console.warn('HCE Load More failed', error);
      const fallback = getPagination(container);
      if (fallback) fallback.hidden = false;
      if (status) status.textContent = hceI18n.loadMoreFailed || 'More products could not be loaded. Pagination is available below.';
      return false;
    } finally {
      button.dataset.loading = 'false';
      button.removeAttribute('aria-busy');
      button.disabled = false;
      button.textContent = originalLabel;
    }
  };

  const restoreBackNavigation = async () => {
    if (restoreStarted) return;
    restoreStarted = true;

    const navEntry = performance.getEntriesByType?.('navigation')?.[0];
    if (navEntry?.type !== 'back_forward') return;

    const saved = readState();
    if (!saved || saved.loadedPages <= 1) return;

    let loadMore = document.querySelector(LOAD_MORE_SELECTOR);
    let currentLoaded = getLoadedPages(loadMore);

    while (loadMore && currentLoaded < saved.loadedPages && loadMore.dataset.nextUrl) {
      const loaded = await loadNext({ restoring: true });
      if (!loaded) break;
      loadMore = document.querySelector(LOAD_MORE_SELECTOR);
      currentLoaded = getLoadedPages(loadMore);
    }

    window.setTimeout(() => {
      window.scrollTo({ top: saved.scrollY, left: 0, behavior: 'auto' });
    }, 60);
  };

  const initObserver = () => {
    if (!document.body) return;

    const observer = new MutationObserver((mutations) => {
      const gridReplaced = mutations.some((mutation) =>
        Array.from(mutation.addedNodes || []).some((node) =>
          node.nodeType === 1 && (
            node.id === 'ProductGridContainer' ||
            node.querySelector?.(CONTAINER_SELECTOR)
          )
        )
      );

      if (!gridReplaced) return;
      window.clearTimeout(mutationTimer);
      mutationTimer = window.setTimeout(enhance, 40);
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.(LOAD_BUTTON_SELECTOR);
    if (button) {
      event.preventDefault();
      loadNext();
      return;
    }

    if (event.target.closest?.(`${GRID_SELECTOR} a[href]`)) saveState();
  });

  document.addEventListener('DOMContentLoaded', () => {
    enhance();
    initObserver();
    restoreBackNavigation();
  });

  document.addEventListener('shopify:section:load', enhance);
  window.addEventListener('pagehide', saveState);
  window.addEventListener('pageshow', (event) => {
    enhance();
    if (event.persisted) prioritizeAvailableProducts();
  });
})();
